import * as React from 'react';
import { PromoModal } from '@cian/frontend-newbuilding-promo-modal-widget';
import { useDispatch, useSelector } from 'react-redux';

import { usePreparedPromo, usePromoDetailsConfig, usePromoDetailsModalTrigger } from './hooks';
import { ActionButtonContainer } from './parts';
import { trackCianMortgageClick } from './tracking';
import { getBuilders, getNewbuildingId, getSellers } from '../../selectors/newbuilding';
import { getHasContacts } from '../../selectors/newbuilding/getHasContacts';
import { selectPromoInfo } from '../../selectors/promoInfo';
import { IPromoInfoSchema } from '../../types/promo';
import { closePromoInfo } from '../../slices/promoInfo';

export const PromoDetailsContainer: React.FC = () => {
  const promo = useSelector(selectPromoInfo);
  const newbuildingId = useSelector(getNewbuildingId);
  const hasContacts = useSelector(getHasContacts);
  const builders = useSelector(getBuilders);
  const sellers = useSelector(getSellers);

  const dispatch = useDispatch();

  const avaliableContacts = React.useMemo(() => {
    if (!promo) {
      return {
        builders: [],
        sellers: [],
      };
    }

    if (!promo.authorRealtyUserId) {
      return {
        builders: builders.filter(contact => contact.showPhone),
        sellers: sellers.filter(contact => contact.showPhone && contact.name),
      };
    }

    return {
      builders: builders.filter(contact => contact.realtyUserId === promo.authorRealtyUserId && contact.showPhone),
      sellers: sellers.filter(
        contact => contact.realtyUserId === promo.authorRealtyUserId && contact.showPhone && contact.name,
      ),
    };
  }, [promo, builders, sellers]);

  const isMultipleContacts = avaliableContacts.sellers.length + avaliableContacts.builders.length > 1;

  const promoModalConfig = usePromoDetailsConfig();
  const preparedPromo = usePreparedPromo(avaliableContacts);

  usePromoDetailsModalTrigger();

  const handleClose = React.useCallback(() => {
    dispatch(closePromoInfo());
  }, [dispatch]);

  const handleMortgageClick = React.useCallback(() => {
    trackCianMortgageClick(promo as IPromoInfoSchema, newbuildingId);
  }, [promo, newbuildingId]);

  if (!promo || !preparedPromo) {
    return null;
  }

  const cianMortgage = promo.promoType === 'cianMortgage';

  return (
    <PromoModal
      open={true}
      onOpenChange={handleClose}
      promo={preparedPromo}
      onCianMortgageClick={handleMortgageClick}
      controls={
        <ActionButtonContainer
          avaliableContacts={avaliableContacts}
          promo={promo}
          hasContacts={hasContacts}
          onMortgageClick={handleMortgageClick}
          cianMortgage={cianMortgage}
        />
      }
      config={promoModalConfig}
      stickyControls={!isMultipleContacts}
    />
  );
};
